import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Trip } from '@/types';
import { useI18n } from '@/hooks/useI18n';

interface TravelPreferencesSectionProps {
  signUpType: 'individual' | 'group';
  preferredApartmentTypeId: string;
  preferredTransportationId: string;
  trip: Trip;
  noApartmentTypesAvailable: boolean;
  noTransportationOptionsAvailable: boolean;
  onApartmentTypeChange: (value: string) => void;
  onTransportationChange: (value: string) => void;
}

const TravelPreferencesSection: React.FC<TravelPreferencesSectionProps> = ({
  signUpType,
  preferredApartmentTypeId,
  preferredTransportationId,
  trip,
  noApartmentTypesAvailable,
  noTransportationOptionsAvailable,
  onApartmentTypeChange,
  onTransportationChange
}) => {
  const { t, formatCurrency } = useI18n();
  
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        🏔️ {t('signup.travelPreferences')} {signUpType === 'group' && t('signup.forEntireGroup')}
      </Typography>
      
      {/* Apartment Type Selection */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth required>
          <InputLabel>🏨 {t('apartment.preferredApartmentType')}</InputLabel>
          <Select
            value={preferredApartmentTypeId}
            onChange={(e) => onApartmentTypeChange(e.target.value)}
            disabled={noApartmentTypesAvailable}
            label={`🏨 ${t('apartment.preferredApartmentType')}`}
          >
            {trip.apartmentTypes?.map((scheme) => (
              <MenuItem key={scheme.id} value={scheme.id}>
                {scheme.name}
              </MenuItem>
            ))}
          </Select>
          {noApartmentTypesAvailable && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {t('signup.noApartmentTypes')}
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* Transportation Selection */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth required>
          <InputLabel>🚌 {t('transportation.transportation')}</InputLabel>
          <Select
            value={preferredTransportationId}
            onChange={(e) => onTransportationChange(e.target.value)}
            disabled={noTransportationOptionsAvailable}
            label={`🚌 ${t('transportation.transportation')}`}
          >
            {trip.transportationOptions?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name} - {formatCurrency(option.pricePerPerson)} / {t('common.person')}
              </MenuItem>
            ))}
          </Select>
          {noTransportationOptionsAvailable && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {t('signup.noTransportation')}
            </Typography>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

export default TravelPreferencesSection;
